import { ROUTES } from "~/utils/routing/routeUtils";

export const SETUP_WALKTHROUGH_STORAGE_KEY = "setup-walkthrough-active";
export const SETUP_WALKTHROUGH_PHASE_KEY = "setup-walkthrough-phase";

/** Core setup chain phases (plus complete for optional visits). */
export type SetupWalkthroughPhase =
  | "property"
  | "building"
  | "unit"
  | "complete";

/**
 * Pathnames where setup walkthrough may auto-resume a tour.
 */
export const isSetupWalkthroughRoute = (pathname: string): boolean => {
  if (
    pathname === ROUTES.propertyTable ||
    pathname === ROUTES.addProperty ||
    pathname === ROUTES.addBuilding ||
    pathname === ROUTES.addUnit ||
    pathname === ROUTES.amenity ||
    pathname === ROUTES.serviceRequest ||
    pathname === ROUTES.payments ||
    pathname === ROUTES.organization
  ) {
    return true;
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 2 && segments[0] === "property") {
    return true;
  }
  if (segments.length === 2 && segments[0] === "building") {
    return true;
  }
  if (segments.length === 2 && segments[0] === "unit") {
    return true;
  }
  if (
    segments.length === 2 &&
    segments[0] === "organization" &&
    segments[1] !== "new"
  ) {
    return true;
  }

  return false;
};

export const isPropertyDetailsPath = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 2 && segments[0] === "property";
};

export const isBuildingDetailsPath = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 2 && segments[0] === "building";
};

export const isUnitDetailsPath = (pathname: string): boolean => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 2 && segments[0] === "unit";
};

export const getPathnameWithoutQuery = (path: string): string =>
  path.split("?")[0] || path;

/**
 * Advance setup phase based on create-form → details redirects.
 */
export const nextSetupPhaseFromNavigation = (
  previousPathname: string,
  nextPathname: string,
  currentPhase: SetupWalkthroughPhase
): SetupWalkthroughPhase => {
  if (
    previousPathname === ROUTES.addProperty &&
    isPropertyDetailsPath(nextPathname)
  ) {
    return "building";
  }
  if (
    previousPathname === ROUTES.addBuilding &&
    isBuildingDetailsPath(nextPathname)
  ) {
    return "unit";
  }
  if (
    previousPathname === ROUTES.addUnit &&
    (isBuildingDetailsPath(nextPathname) || isUnitDetailsPath(nextPathname))
  ) {
    return "complete";
  }
  return currentPhase;
};

export const readSetupWalkthroughActive = (): boolean => {
  try {
    return sessionStorage.getItem(SETUP_WALKTHROUGH_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

export const writeSetupWalkthroughActive = (active: boolean): void => {
  try {
    if (active) {
      sessionStorage.setItem(SETUP_WALKTHROUGH_STORAGE_KEY, "true");
    } else {
      sessionStorage.removeItem(SETUP_WALKTHROUGH_STORAGE_KEY);
      sessionStorage.removeItem(SETUP_WALKTHROUGH_PHASE_KEY);
    }
  } catch {
    // ignore storage errors (private mode, etc.)
  }
};

export const readSetupWalkthroughPhase = (): SetupWalkthroughPhase => {
  try {
    const value = sessionStorage.getItem(SETUP_WALKTHROUGH_PHASE_KEY);
    if (
      value === "property" ||
      value === "building" ||
      value === "unit" ||
      value === "complete"
    ) {
      return value;
    }
  } catch {
    // ignore
  }
  return "property";
};

export const writeSetupWalkthroughPhase = (
  phase: SetupWalkthroughPhase
): void => {
  try {
    sessionStorage.setItem(SETUP_WALKTHROUGH_PHASE_KEY, phase);
  } catch {
    // ignore
  }
};
